import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, Copy, X } from "lucide-react";
import { ApiError } from "./api";

export function Status({ value }: { value: string }) {
  const tone = ["ACTIVE", "PUBLISHED", "SUCCEEDED", "READY", "USED", "ACCEPTED", "DELIVERED"].includes(value)
    ? "positive"
    : ["FAILED", "PARTIAL_FAILED", "INVALID", "DISABLED", "DEAD_LETTER", "REVOKED", "CANCELLED"].includes(value)
      ? "critical"
      : ["DRAFT", "WARMING", "PENDING", "QUEUED", "LEASED", "RUNNING", "UPLOADING", "CANCEL_REQUESTED", "DRAINING", "CLOUD_REQUESTED", "WAITING_CAPACITY", "WAITING_NODE_READY", "WAITING_INSTANCE_REMOVAL", "ROLLBACK_REQUESTED", "WAITING_ROLLBACK", "CLEANUP_PENDING", "EXTERNAL_CLEANUP_REQUIRED"].includes(value)
        ? "warning"
        : "neutral";
  return <span className={`status status--${tone}`}><span aria-hidden="true" />{value}</span>;
}

export function ErrorNotice({ error }: { error: unknown }) {
  const message = error instanceof ApiError
    ? `${error.message}${error.retryable ? "，请稍后重试。" : "。"}${error.requestId ? ` Request ID: ${error.requestId}` : ""}`
    : "请求没有完成，请检查控制服务状态后重试。";
  return <div className="notice notice--error" role="alert"><AlertTriangle size={17} />{message}</div>;
}

export function LoadingRows({ columns = 4 }: { columns?: number }) {
  return <div className="loading-rows" aria-label="正在读取数据">
    {[0, 1, 2].map((row) => <div className="loading-row" key={row} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {Array.from({ length: columns }).map((_, cell) => <span key={cell} />)}
    </div>)}
  </div>;
}

type ConfirmProps = {
  open: boolean;
  title: string;
  target: string;
  confirmLabel: string;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
};

export function Modal({ open, onClose, className = "", label, children }: {
  open: boolean;
  onClose: () => void;
  className?: string;
  label: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;
  return createPortal(
    <div className="modal-layer" role="presentation">
      <section className={`dialog ${className}`.trim()} role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </section>
    </div>,
    document.body,
  );
}

export function ReasonDialog(props: ConfirmProps) {
  return <Modal open={props.open} onClose={props.onClose} label={props.title}>
    <form onSubmit={(event) => {
      event.preventDefault();
      const reason = new FormData(event.currentTarget).get("reason")?.toString() ?? "";
      props.onConfirm(reason);
    }}>
      <button className="icon-button dialog__close" type="button" onClick={props.onClose} aria-label="关闭"><X size={18} /></button>
      <p className="eyebrow">需要操作原因</p>
      <h2>{props.title}</h2>
      <p className="dialog__target">目标：<strong>{props.target}</strong></p>
      <label>原因<textarea name="reason" minLength={3} maxLength={500} required autoFocus placeholder="记录为什么现在执行此操作" /></label>
      <div className="dialog__actions">
        <button type="button" className="button button--quiet" onClick={props.onClose}>继续查看</button>
        <button type="submit" className="button button--primary" disabled={props.pending}>{props.pending ? "正在执行…" : props.confirmLabel}</button>
      </div>
    </form>
  </Modal>;
}

export function SecretDialog({ secret, onClose }: { secret: string | null; onClose: () => void }) {
  return <Modal open={Boolean(secret)} onClose={onClose} className="dialog--secret" label="保存 API Key">
    <div>
      <p className="eyebrow"><Check size={15} />密钥已生成</p>
      <h2>现在保存 API Key</h2>
      <p>关闭后无法再次查看完整值。建议立即存入业务密钥管理系统。</p>
      <output className="secret-value">{secret}</output>
      <div className="dialog__actions">
        <button className="button button--quiet" onClick={() => secret && navigator.clipboard.writeText(secret)}><Copy size={16} />复制密钥</button>
        <button className="button button--primary" onClick={onClose}>我已安全保存</button>
      </div>
    </div>
  </Modal>;
}

export function EmptyState({ title, children }: { title: string; children: ReactNode }) {
  return <div className="empty"><h3>{title}</h3><p>{children}</p></div>;
}

export function formatTime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}
